import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/styles";
import { Form, Formik, FormikProps } from "formik";
import React from "react";
import * as Yup from "yup";
import CustomButton from "../../../components/Button/CustomButton";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import CustomDropdownMultiple from "../../../components/Input/CustomDropdownMultiple";
import StyledTooltip from "../../../components/Wrapper/StyledTooltip";
import { UserAssignmentData, UserAssignmentType } from "./models/userAssignment";
import useAssignmentDialog from "./useAssignmentDialog";

const useStyles = makeStyles({
  header: {
    textTransform: "uppercase",
  },
});

type FormType = {
  attributes: string[];
};

type Primitive = string | number | boolean;

function arrayEquals(arr1: Primitive[], arr2: Primitive[]) {
  return arr1.length === arr2.length && arr1.reduce<boolean>((isEqual, curr) => isEqual && arr2.includes(curr), true);
}

interface UserAssignmentDialogProps<T extends UserAssignmentData<UserAssignmentType>> {
  currentName: string;
  currentValue: T;
  onConfirm: <UAT extends UserAssignmentType>(data: UserAssignmentData<UAT>) => void;
  onClose: () => void;
  open: boolean;
}

function UserAssignmentDialog<T extends UserAssignmentData<UserAssignmentType>>({
  currentName,
  currentValue,
  onConfirm: handleConfirm,
  onClose: handleClose,
  open,
}: UserAssignmentDialogProps<T>) {
  const classes = useStyles();
  const initialValues = {
    attributes: currentValue?.data || [],
  };
  const { description, disabledHint, label, options, optionsDictionary, title } = useAssignmentDialog(
    currentValue.type,
  );

  const renderForm = ({ handleChange, setFieldTouched, touched, values }: FormikProps<FormType>) => {
    const buttonDisabled = !touched.attributes || arrayEquals(values.attributes, initialValues.attributes);

    const handleDropdownChange = (event: React.ChangeEvent<any>) => {
      handleChange(event);
      setFieldTouched("attributes", true);
    };

    return (
      <Form>
        <Box pb={4}>
          <Grid container justify="flex-start" direction="row">
            <CustomDropdownMultiple
              chips
              fullWidth
              labelText={label}
              name="attributes"
              onChange={handleDropdownChange}
              options={options}
              optionsDictionary={optionsDictionary}
              value={values.attributes}
            />
          </Grid>
        </Box>
        <Grid container item direction="row" justify="flex-end">
          <StyledTooltip title={(buttonDisabled && disabledHint) || ""}>
            <Box>
              <CustomButton disabled={buttonDisabled} primary text="Apply" type="submit" />
            </Box>
          </StyledTooltip>
        </Grid>
      </Form>
    );
  };

  const descriptionTemplate = () => {
    return description.replace("<user>", currentName);
  };

  return (
    <CustomDialog fullWidth maxWidth="md" onClose={(): void => handleClose()} open={open}>
      <h2 className={classes.header}>{title}</h2>
      <p>{descriptionTemplate()}</p>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object({})}
        onSubmit={(values): void => {
          if (currentValue) {
            handleConfirm({ data: values.attributes, type: currentValue.type });
            handleClose();
          }
        }}
      >
        {renderForm}
      </Formik>
    </CustomDialog>
  );
}

export default UserAssignmentDialog;
